import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { themeColors } from "../../../Config/Themes/theme";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useMapStore } from "../../../Stores/MapStore";
import {
  formatDateInDateMonthYear,
  formatDateInDateMonthYearHour,
} from "../../../Common/formatDate/formatDate";
import LineChart from "../../../Components/Charts/LineChart/LineChart";
import Select from "react-select";
import { toJS } from "mobx";
import NoDataCard from "../../../Components/NoDataCard/NoDataCard";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import AnimateButton from "../../../Components/Theme/Extended/AnimateButton";
import SubmitButton from "../../../Components/Theme/Extended/SubmitButton";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

const PeriodicFullnessChart = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = themeColors(theme.palette.mode);

  const {
    initializeData,
    resetFilters,
    historicMapStartDate,
    historicMapEndDate,
    filteredHistoricTrashBinsFromDateToDateList,
    setSelectedTrashBinId,
    getTrashBinsEventsFromDateToDate,
    shouldFetchHistoricMapData,
    trashBinsEventsFromDateToDateList,
    selectedTrashBinId,
  } = useMapStore();

  const [isClearable, setIsClearable] = useState(false);
  const [isSearchable, setIsSearchable] = useState(true);
  const [isSelectedFromMap, setIsSelectedFromMap] = useState([]);
  const [componentList, setComponentList] = useState([]);
  // const rgb = colors.primary.main.match(/\w\w/g).map((x) => parseInt(x, 16));

  const selectedTrashBinFromMap =
    selectedTrashBinId.length > 0
      ? [
          {
            value: selectedTrashBinId[0]?.id,
            label: selectedTrashBinId[0]?.address,
          },
        ]
      : [];

  const addNewChart = () => {
    const newComponent = {
      selectedTrashBinId: [],
      graphData: { labels: [], datasets: [] },
    };
    const newComponentListData = [...componentList, newComponent];
    // console.log("newComponentListData", newComponentListData);
    setComponentList(newComponentListData);
  };

  const removeNewChart = (index) => {
    setComponentList((prevList) => {
      const updatedList = [...prevList];
      updatedList.splice(index, 1);
      // console.log("updatedList", updatedList);
      return updatedList;
    });
  };

  const handleOnChange = (events) => {
    let updatedSelection = [...isSelectedFromMap];

    if (events && events.length > 0) {
      events.forEach((event) => {
        const existsInSelection = updatedSelection.find(
          (selectedBin) => selectedBin.value === event.value
        );
        if (!existsInSelection) {
          updatedSelection.push(event);
        }
      });
      setIsClearable(true);
    } else {
      setIsClearable(false);
      updatedSelection = [];
      initializeData();
    }
    setSelectedTrashBinId(updatedSelection);
  };

  const getRandomRGBColor = (alpha) => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const initializeGraphData = () => {
    const datasets = [];
    const labelsSet = new Set();

    const uniqueBins = [
      ...new Set(
        filteredHistoricTrashBinsFromDateToDateList.map((item) => item.id)
      ),
    ];

    uniqueBins.forEach((binId) => {
      const binData = filteredHistoricTrashBinsFromDateToDateList.find(
        (historicTrashBin) => historicTrashBin.id === binId
      );

      if (binData.telemetryTrashBinEvents?.length > 0) {
        const data = binData.telemetryTrashBinEvents.map((event) => {
          const label = formatDateInDateMonthYearHour(event.timestamp);
          labelsSet.add(label);
          return {
            x: label,
            y: event.binPercentageFullness,
          };
        });

        const dataset = {
          label: "Id: " + binData.id + " Name: " + binData.name,
          data: data,
          lineTension: 0.4,
          fill: false,
          id: binData.id,
          address: binData.address,
          backgroundColor: getRandomRGBColor(1),
          borderColor: getRandomRGBColor(0.2),
        };

        datasets.push(dataset);
      }
    });

    const labels = [...labelsSet].sort();

    return {
      labels: labels,
      datasets: datasets,
    };
  };

  const initializeNewChartGraphData = (selectedTrashBinIds = []) => {
    const datasets = [];
    const labelsSet = new Set();

    if (selectedTrashBinIds?.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Handle the case where trash bins are selected
    const mySelectedTrashBins = trashBinsEventsFromDateToDateList.filter(
      (trashBin) =>
        selectedTrashBinIds.some(
          (selectedTrashBin) => selectedTrashBin.value === trashBin.id
        )
    );

    mySelectedTrashBins.forEach((selectedTrashBin) => {
      const binData = trashBinsEventsFromDateToDateList.find(
        (historicTrashBin) => historicTrashBin.id === selectedTrashBin.id
      );

      if (binData.telemetryTrashBinEvents?.length > 0) {
        const data = binData.telemetryTrashBinEvents.map((event) => {
          const label = formatDateInDateMonthYearHour(event.timestamp);
          labelsSet.add(label);
          return {
            x: label,
            y: event.binPercentageFullness,
          };
        });

        const dataset = {
          label: "Id: " + binData.id + " Name: " + binData.name,
          data: data,
          lineTension: 0.4,
          fill: false,
          id: binData.id,
          address: binData.address,
          backgroundColor: getRandomRGBColor(1),
          borderColor: getRandomRGBColor(0.2),
        };

        datasets.push(dataset);
      }
    });

    const labels = [...labelsSet].sort();

    return {
      labels: labels,
      datasets: datasets,
    };
  };

  const handleAlternativeSelect = (events, index) => {
    if (events) {
      const selectedOptions = Array.isArray(events) ? events : [events];

      const selectedTrashBinIds = selectedOptions.map((option) => ({
        value: option.value,
        label: option.label,
      }));

      const graphData = initializeNewChartGraphData(selectedTrashBinIds);

      const updatedComponentList = [...componentList];
      updatedComponentList[index] = {
        ...updatedComponentList[index],
        selectedTrashBinId: selectedTrashBinIds,
        graphData: graphData,
      };
      setComponentList(updatedComponentList);
      setIsClearable(true);
    } else {
      setIsClearable(false);
      setComponentList([]);
    }
  };

  useEffect(() => {
    initializeData();
    setIsSelectedFromMap(selectedTrashBinFromMap);
    // console.log("selectedTrashBinFromMap", toJS(selectedTrashBinFromMap));
    // console.log("selectedTrashBinId", toJS(selectedTrashBinId));
  }, [selectedTrashBinId?.splice]);

  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [],
  });

  const [newOptions, setNewOptions] = useState(
    trashBinsEventsFromDateToDateList
      .map((item) => ({
        value: item.id,
        label: item.address,
        isFixed: true,
      }))
      .sort((a, b) => a.value - b.value)
  );

  // useEffect(() => {
  //   console.log("updatedComponentList", componentList);
  // }, [componentList]);

  useEffect(() => {
    initializeData();
    initializeGraphData();
    setIsSelectedFromMap(selectedTrashBinFromMap);
    // console.log("****** selectedTrashBinId", toJS(selectedTrashBinId));
  }, [selectedTrashBinId?.splice]);

  useEffect(() => {
    if (isSelectedFromMap.length > 0) {
      setSelectedTrashBinId(isSelectedFromMap);
    }
  }, [isSelectedFromMap]);

  useEffect(() => {
    if (selectedTrashBinId.length === 0 && isSelectedFromMap.length === 0) {
      initializeData();
    }
    setIsSelectedFromMap(selectedTrashBinFromMap);
  }, [selectedTrashBinId?.splice]);

  // useEffect(() => {
  //   console.log("componentList", componentList);
  // }, [componentList?.splice]);

  useEffect(() => {
    setNewOptions(
      trashBinsEventsFromDateToDateList
        .map((item) => ({
          value: item.id,

          // label: item.address,
          label: item.id + " - " + item.address,
        }))
        .sort((a, b) => a.value - b.value)
    );
  }, [trashBinsEventsFromDateToDateList]);

  // const exportToPDF = () => {
  //   const chartContainer = document.querySelector(".chart-container");

  //   html2canvas(chartContainer).then((canvas) => {
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF();

  //     const imgProps = pdf.getImageProperties(imgData);
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

  //     pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

  //     pdf.save(t("reports.reports"));
  //   });
  // };

  // const exportToPDF = () => {
  //   const chartContainer = document.querySelector(".chart-container");

  //   html2canvas(chartContainer).then((canvas) => {
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF();

  //     const imgProps = pdf.getImageProperties(imgData);
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

  //     let position = 0;
  //     const pageHeight = pdf.internal.pageSize.getHeight();

  //     while (position < imgProps.height) {
  //       pdf.addPage();
  //       pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);

  //       position += pageHeight;
  //       // Break the loop if all content has been rendered
  //       if (position >= imgProps.height) {
  //         break;
  //       }
  //     }

  //     pdf.save(t("reports.reports"));
  //   });
  // };

  const exportToPDF = async () => {
    const chartContainers = document.querySelectorAll(".chart-container");

    const pdfDoc = await PDFDocument.create();

    for (const chartContainer of chartContainers) {
      const canvas = await html2canvas(chartContainer);

      const page = pdfDoc.addPage([canvas.width, canvas.height]);

      const pngImage = await pdfDoc.embedPng(canvas.toDataURL("image/png"));
      const pngDims = pngImage.scale(1);

      page.drawImage(pngImage, {
        x: 0,
        y: 0,
        width: pngDims.width,
        height: pngDims.height,
      });
    }

    const pdfBytes = await pdfDoc.save();

    // Download the PDF
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(
      new Blob([pdfBytes], { type: "application/pdf" })
    );
    downloadLink.download = t("reports.reports");
    downloadLink.click();
  };

  useEffect(() => {
    resetFilters();
    initializeData();
  }, []);

  useEffect(() => {
    getTrashBinsEventsFromDateToDate(
      historicMapStartDate,
      historicMapEndDate
    ).then(() => {
      const newGraphData = initializeGraphData();
      setGraphData(newGraphData);
    });
  }, [
    filteredHistoricTrashBinsFromDateToDateList?.length,
    shouldFetchHistoricMapData === true,
    selectedTrashBinId,
    isSelectedFromMap,
  ]);

  return (
    <div className="mt-6 chart-container">
      <div>
        <div className="flex items-center justify-end mr-2 mb-2">
          <AnimateButton>
            <SubmitButton
              sx={{ backgroundColor: colors.greenAccent.main }}
              className="capitalize text-base mr-2"
              buttonTitle="addComponent"
              style={{
                background: colors.greenAccent.main,
              }}
              disableRipple
              disableElevation
              type="submit"
              onClick={addNewChart}
            />
          </AnimateButton>
          <AnimateButton>
            <SubmitButton
              sx={{ backgroundColor: colors.greenAccent.main }}
              className="capitalize text-base"
              buttonTitle="exportToPdf"
              style={{
                background: colors.greenAccent.main,
              }}
              disableRipple
              disableElevation
              type="submit"
              onClick={exportToPDF}
            />
          </AnimateButton>
        </div>

        <>
          <Select
            defaultValue={selectedTrashBinFromMap}
            isMulti
            className="basic-multi-select"
            classNamePrefix="select"
            isClearable={isClearable}
            isSearchable={isSearchable}
            options={newOptions}
            onChange={handleOnChange}
            placeholder={t("trashBins.ChooseTrashbins")}
          />
          <div className="mt-[0.5rem]">
            {filteredHistoricTrashBinsFromDateToDateList?.length ? (
              <LineChart
                data={graphData}
                title={`${t(
                  "reports.SelectedDates"
                )}: ${formatDateInDateMonthYear(
                  historicMapStartDate
                )} - ${formatDateInDateMonthYear(historicMapEndDate)}`}
              />
            ) : (
              <NoDataCard />
            )}
          </div>
        </>

        {componentList?.map((component, index) => {
          return (
            <div className="mt-4" key={index}>
              <div className="flex justify-end items-end mb-2">
                <RemoveCircleIcon
                  fontSize="large"
                  color="primary"
                  className="cursor-pointer"
                  onClick={() => removeNewChart(index)}
                />
              </div>
              <div className="mt-[0.7rem]">
                <Select
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isClearable={isClearable}
                  isSearchable={isSearchable}
                  options={newOptions}
                  onChange={(events) => handleAlternativeSelect(events, index)}
                  placeholder={t("trashBins.ChooseTrashbins")}
                />
              </div>
              <div className="mt-[0.5rem]">
                {/* {console.log("component graphData", component.graphData)} */}
                {component?.graphData?.datasets?.length ? (
                  <LineChart
                    key={index}
                    data={component?.graphData}
                    title={`${t(
                      "reports.SelectedDates"
                    )}: ${formatDateInDateMonthYear(
                      historicMapStartDate
                    )} - ${formatDateInDateMonthYear(historicMapEndDate)}`}
                  />
                ) : (
                  <NoDataCard />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default observer(PeriodicFullnessChart);
